import { deserialize } from "serializr";
import axiosInstance from "../../interceptor/axiosInstance";
import _axiosInstance from "../../interceptor/_axiosInstance";
import * as ApiRoutes from "../../routes/routeConstants/apiRoutes";
import { StudentMetricsModel } from "../../models/DashboardModel/StudentMetricsModel/studentMetrics.model";
import { ContentAccessedModel } from "../../models/DashboardModel/ContentAccessedModel/contentAccessed.model";
import { MostWatchedContentModel } from "../../models/DashboardModel/MostWatchedContentModel/mostWatchedContent.model";
import { MostLikedContentModel } from "../../models/DashboardModel/MostLikedContentModel/mostLikedContent.model";
import { MostDislikedContentModel } from "../../models/DashboardModel/MostDislikedContentModel/mostDislikedContent.model";
import { MostAttemptedQuizModel } from "../../models/DashboardModel/MostAttemptedQuizModel/mostAttemptedQuiz.model";
import { StudentActivitiesModel } from "../../models/DashboardModel/StudentActivitiesModel/studentActivities.model";
import { MetaModel } from "../../models/CourseModels/SubjectModel/subject.model";
import { StudentPerformanceModel, StudentGradePerformanceModel } from "../../models/DashboardModel/StudentPerformanceModel/studentPerformance.model";
import { DashboardFilterModel } from "../../models/DashboardModel/DashboardFilterModel/dashboardFilter.model";
import { _DashboardFilterModel } from "../../models/DashboardModel/DashboardFilterModel/_dashboardFilter.model";
import { StudentStatusModel } from "../../models/DashboardModel/StudentStatusModel/studentStatus.model";
import { TutorActivitiesModel } from "../../models/DashboardModel/TutorActivitiesModel/tutorActivities.model";


export default class DashboardService {

  static fetchStudentStatus(
    onSuccess: (value: StudentStatusModel) => void,
    onError: Function,
    onFinal: () => void
  ) {
    return _axiosInstance
      .get(ApiRoutes.DASHBOARD_STUDENT_STATUS)
      .then((response) => {
        const data = deserialize(
          StudentStatusModel,
          response?.data
        );
        onSuccess(data);
      })
      .catch((e) => {
        onError(e);
      })
      .finally(onFinal);
  }


  static fetchStudentMetrics(
    onSuccess: (value: StudentMetricsModel) => void,
    onError: Function,
    onFinal: () => void
  ) {
    return axiosInstance
      .get(ApiRoutes.DASHBOARD_STUDENT_METRICS)
      .then((response) => {
        const data = deserialize(
          StudentMetricsModel,
          response?.data?.["metrics"]
        );
        onSuccess(data);
      })
      .catch((e) => {
        onError(e);
      })
      .finally(onFinal);
  }


  static downloadStudentContentMetrics(
    onSuccess: (value: any) => void,
    onError: Function,
    onFinal: () => void
  ) {
    return _axiosInstance
      .get(ApiRoutes.DOWNLOAD_CONTENT_METRICS)
      .then((response) => new Blob([response.data]))
      .then((blob) => {
        // Create blob link to download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `Report.csv`,
        );
        // Append to html link element page
        document.body.appendChild(link);

        // Start download
        link.click();

        // Clean up and remove the link
        link.parentNode?.removeChild(link);

        onSuccess("Download");
      })
      .catch((e) => {
        onError(e);
      })
      .finally(onFinal);
  }

  static fetchContentAccessed(
    params: DashboardFilterModel | undefined,
    onSuccess: (value: ContentAccessedModel) => void,
    onError: Function,
    onFinal: () => void
  ) {
    return axiosInstance
      .get(ApiRoutes.DASHBOARD_CONTENT_ACCESSED, {
        params,
      })
      .then((response) => {
        const data = deserialize(ContentAccessedModel, response?.data);
        onSuccess(data);
      })
      .catch((e) => {
        onError(e);
      })
      .finally(onFinal);
  }

  static fetchMostWatchedContent(
    params: _DashboardFilterModel | undefined,
    onSuccess: (value: MostWatchedContentModel[]) => void,
    onError: Function,
    onFinal: () => void
  ) {
    return _axiosInstance
      .get(ApiRoutes.DASHBOARD_MOST_WATCHED_CONTENT, {
        params: { ...params, action: '1' }
      })
      .then((response) => {
        const data = deserialize(
          MostWatchedContentModel,
          response?.data
        ) as MostWatchedContentModel[];
        onSuccess(data);
      })
      .catch((e) => {
        onError(e);
      })
      .finally(onFinal);
  }

  static fetchMostLikedContent(
    params: _DashboardFilterModel | undefined,
    onSuccess: (value: MostLikedContentModel[]) => void,
    onError: Function,
    onFinal: () => void
  ) {
    return _axiosInstance
      .get(ApiRoutes.DASHBOARD_MOST_LIKED_CONTENT, {
        params: { ...params, action: '3' }
      })
      .then((response) => {
        const data = deserialize(
          MostLikedContentModel,
          response?.data
        ) as MostLikedContentModel[];
        onSuccess(data);
      })
      .catch((e) => {
        onError(e);
      })
      .finally(onFinal);
  }

  static fetchMostDislikedContent(
    params: _DashboardFilterModel | undefined,
    onSuccess: (value: MostDislikedContentModel[]) => void,
    onError: Function,
    onFinal: () => void
  ) {
    return _axiosInstance
      .get(ApiRoutes.DASHBOARD_MOST_DISLIKED_CONTENT, {
        params: { ...params, action: '4' }
      })
      .then((response) => {
        const data = deserialize(
          MostDislikedContentModel,
          response?.data
        ) as MostDislikedContentModel[];
        onSuccess(data);
      })
      .catch((e) => {
        onError(e);
      })
      .finally(onFinal);
  }
  static fetchMostAttemptedQuiz(
    params: _DashboardFilterModel | undefined,
    onSuccess: (value: MostAttemptedQuizModel[]) => void,
    onError: Function,
    onFinal: () => void
  ) {
    return _axiosInstance
      .get(ApiRoutes.DASHBOARD_MOST_ATTEMPTED_QUIZ, {
        params: { ...params, action: '2' }
      })
      .then((response) => {
        const data = deserialize(
          MostAttemptedQuizModel,
          response?.data
        ) as MostAttemptedQuizModel[];
        onSuccess(data);
      })
      .catch((e) => {
        onError(e);
      })
      .finally(onFinal);
  }
  static fetchStudentActivities(
    params: _DashboardFilterModel | undefined,
    onSuccess: (value: StudentActivitiesModel) => void,
    onError: Function,
    onFinal: () => void
  ) {
    return _axiosInstance
      .get(ApiRoutes.DASHBOARD_STUDENT_ACTIVITIES, { params })
      .then((response) => {
        const data = deserialize(StudentActivitiesModel, response?.data);
        onSuccess(data);
      })
      .catch((e) => {
        onError(e);
      })
      .finally(onFinal);
  }

  static fetchTutorActivities(
    params: _DashboardFilterModel | undefined,
    onSuccess: (value: TutorActivitiesModel) => void,
    onError: Function,
    onFinal: () => void
  ) {
    return axiosInstance
      .get(ApiRoutes.DASHBOARD_TUTOR_ACTIVITIES, { params })
      .then((response) => {
        const data = deserialize(TutorActivitiesModel, response?.data);
        onSuccess(data);
      })
      .catch((e) => {
        onError(e);
      })
      .finally(onFinal);
  }

  static fetchStudentPerformance(
    params: _DashboardFilterModel | undefined,
    onSuccess: (value: StudentPerformanceModel[]) => void,
    onError: Function,
    onFinal: () => void
  ) {
    return _axiosInstance
      .get(ApiRoutes.DASHBOARD_STUDENT_PERFORMANCE, { params })
      .then((response) => {
        const data = deserialize(
          StudentPerformanceModel,
          response?.data?.gradeData
        ) as StudentPerformanceModel[];
        onSuccess(data);
      })
      .catch((e) => {
        onError(e);
      })
      .finally(onFinal);
  }

  static fetchStudentPerformanceByGrade(
    params: _DashboardFilterModel | undefined,
    onSuccess: (value: StudentGradePerformanceModel[]) => void,
    onError: Function,
    onFinal: () => void
  ) {
    return _axiosInstance
      .get(ApiRoutes.DASHBOARD_STUDENT_PERFORMANCE_BY_GRADE, { params })
      .then((response) => {
        const data = deserialize(
          StudentGradePerformanceModel,
          response?.data?.sectionData
        ) as StudentGradePerformanceModel[];
        onSuccess(data);
      })
      .catch((e) => {
        onError(e);
      })
      .finally(onFinal);
  }

  static downloadStudentPerformanceByGrade(
    params: _DashboardFilterModel | undefined,
    onSuccess: (value: any) => void,
    onError: Function,
    onFinal: () => void
  ) {
    return _axiosInstance
      .get(ApiRoutes.DOWNLOAD_STUDENT_PERFORMANCE_BY_GRADE, { params })
      .then((response) => new Blob([response.data]))
      .then((blob) => {
        // Create blob link to download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `Report.csv`,
        );
        // Append to html link element page
        document.body.appendChild(link);

        // Start download
        link.click();

        // Clean up and remove the link
        link.parentNode?.removeChild(link);

        onSuccess("Download");
      })
      .catch((e) => {
        onError(e);
      })
      .finally(onFinal);
  }




  static fetchLoginReport(
    params: _DashboardFilterModel | undefined,
    onSuccess: (value: any) => void,
    onError: Function,
    onFinal: () => void
  ) {
    return _axiosInstance
      .get(ApiRoutes.DASHBOARD_LOGIN_REPORT, { params })
      .then((response) => new Blob([response.data]))
      .then((blob) => { onSuccess(blob) })
      .catch((e) => {
        onError(e);
      })
      .finally(onFinal);
  }

  static fetchStudentLoginReport(
    params: _DashboardFilterModel | undefined,
    onSuccess: (value: any) => void,
    onError: Function,
    onFinal: () => void
  ) {
    return _axiosInstance
      .get(ApiRoutes.STUDENT_LOGIN_REPORT, { params })
      .then((response) => onSuccess(response.data))
      .catch((e) => {
        onError(e);
      })
      .finally(onFinal);
  }
  static fetchTutorLoginReport(
    params: _DashboardFilterModel | undefined,
    onSuccess: (value: any) => void,
    onError: Function,
    onFinal: () => void
  ) {
    return _axiosInstance
      .get(ApiRoutes.DASHBOARD_TUTOR_LOGIN_REPORT, { params })
      .then((response) => new Blob([response.data]))
      .then((blob) => {
        onSuccess(blob);
      })
      .catch((e) => {
        onError(e);
      })
      .finally(onFinal);
  }

  }
